import { NetworkStatus } from '../../../core/business-objects/network/network-status';
import { AccessGroup } from '../../../vas/business-objects/access-group/access-group.model';
import { Account } from '../../../vas/business-objects/account/account.model';
import { Control } from '../../../vas/business-objects/control/control.model';
import { Field } from '../../../vas/business-objects/field/field.model';
import { File } from '../../../vas/business-objects/file/file.model';
import { JobStatus } from '../../../vas/business-objects/job-status/job-status.model';
import { JobType } from '../../../vas/business-objects/job-type/job-type.model';
import { IJobPointer, Job, JobPointerEnum } from '../../../vas/business-objects/job/job.model';
import { Membership } from '../../../vas/business-objects/membership/membership.model';
import { Note } from '../../../vas/business-objects/note/note.model';
import { Report } from '../../../vas/business-objects/report/report.model';
import { Task } from '../../../vas/business-objects/task/task.model';
import { User } from '../../../vas/business-objects/user/user.model';
import { TextControlConfig } from '../../../vas/controls/text/text-control-config.model';
import { JobPointerModel } from '../../atoms/job-card-pointers/job-pointer.model';

export interface IJobListItemModelArgs {
    job: Job;
    accessGroup: AccessGroup;
    accessGroups: Array<AccessGroup>;
    activeAccount: Account;
    activeUser: User;
    activeMembership: Membership;
    assigneeUser: User;
    controls: Array<Control>;
    createdByUser: User;
    fields: Array<Field>;
    files: Array<File>;
    jobStatus: JobStatus;
    jobType: JobType;
    modifiedByUser: User;
    notes: Array<Note>;
    reports: Array<Report>;
    tasks: Array<Task>;
    networkStatus: NetworkStatus;
    changesPending: boolean;

}

export interface IJobListItemModelGetInstanceArgs {
    job: Job;
    activeAccount: Account;
    activeUser: User;
    activeMembership: Membership;
    accessGroups: Array<AccessGroup>;
    controls: Array<Control>;
    fields: Array<Field>;
    files: Array<File>;
    jobStatuses: Array<JobStatus>;
    jobTypes: Array<JobType>;
    notes: Array<Note>;
    reports: Array<Report>;
    tasks: Array<Task>;
    users: Array<User>;
    networkStatus: NetworkStatus;
    changesPending: boolean;
}

const defaults = () => ({
    job: undefined,
    accessGroup: undefined,
    activeAccount: undefined,
    activeUser: undefined,
    activeMembership: undefined,
    assigneeUser: undefined,
    controls: [],
    createdByUser: undefined,
    fields: [],
    files: [],
    jobStatus: undefined,
    jobType: undefined,
    modifiedByUser: undefined,
    notes: [],
    reports: [],
    tasks: [],
    networkStatus: undefined,
    changesPending: false
});

export class JobCardModel {
    protected _job: Job;
    protected _accessGroup: AccessGroup;
    protected _activeAccount: Account;
    protected _activeUser: User;
    protected _activeMembership: Membership;
    protected _assigneeUser: User;
    protected _controls: Array<Control>;
    protected _createdByUser: User;
    protected _fields: Array<Field>;
    protected _files: Array<File>;
    protected _jobStatus: JobStatus;
    protected _jobType: JobType;
    protected _modifiedByUser: User;
    protected _notes: Array<Note>;
    protected _reports: Array<Report>;
    protected _tasks: Array<Task>;
    protected _networkStatus: NetworkStatus;
    protected _changesPending: boolean;

    /**
     * This method allows us to easily make an instance of the JobListItemModel by passing in a job and lists of Users,
     * JobStatuses, AccessGroups etc etc.
     *
     * The method will filter these lists correctly to ensure the JobListItemModel only contains references to the Users,
     * JobStatuses, AccessGroups etc etc that are actually related to the job.
     */
    static getInstance(args: IJobListItemModelGetInstanceArgs): JobCardModel {

        return new JobCardModel({
            ...args,
            accessGroup: args.accessGroups.find(accessGroup => accessGroup.id === args.job.accessGroup),
            assigneeUser: args.users.find(user => user.id === args.job.assigneeId),
            createdByUser: args.users.find(user => user.id === args.job.createdBy),
            fields: args.job.fieldIds
                .map(id => args.fields.find(field => field.id === id))
                .filter(object => !!object),
            files: args.job.fileIds
                .map(id => args.files.find(file => file.id === id))
                .filter(object => !!object),
            jobStatus: args.jobStatuses.find(jobStatus => jobStatus.id === args.job.jobStatus),
            jobType: args.jobTypes
                .find(jobType => jobType.id === args.job.jobType),
            modifiedByUser: args.users.find(user => user.id === args.job.modifiedBy),
            notes: args.notes.filter(note => note.job === args.job.id),
            reports: args.reports.filter(report => report.job === args.job.id),
            tasks: args.tasks.filter(task => task.job === args.job.id)
        });
    }

    constructor(args: IJobListItemModelArgs = {} as any) {

        args = {...defaults(), ...args};

        Object.keys(args).forEach(key => {
            this[`_${key}`] = args[key];
        });
    }

    get canClone(): boolean {
        return this._networkStatus.isOnline() && this.userIsAdmin;
    }

    get canNavigate(): boolean {
        return this.jobLocation ? this.jobLocation.length > 0 : false;
    }

    get canPrint(): boolean {
        return this._networkStatus.isOnline();
    }

    get canRemove(): false {
        return false;
    }

    get job(): Job {
        return this._job;
    }

    get fields(): Array<Field> {
        return this._fields || [];
    }

    get jobAccessGroupTitle(): string {
        return this._accessGroup ? this._accessGroup.title : undefined;
    }

    get jobAccountId(): string {
        return this.job ? this.job.account : undefined;
    }

    get jobAssigneeName(): string {
        return this._assigneeUser ? this._assigneeUser.name : '';
    }

    get jobDate(): string {
        return this.job ? this.job.jobDate : '';
    }

    get jobId(): string {
        return this.job ? this.job.id : undefined;
    }

    get jobLocation(): string {

        const location = this.jobLocationByConfig;

        if (!location) {
            return this.jobLocationByControlName;
        }

        return location;
    }

    protected get jobLocationByControlName(): string {
        return this.getValueByControlName('fields.inspectionLocation');
    }

    protected get jobLocationByConfig(): string {
        const control = this._controls.find(_control => (_control.config as TextControlConfig).isLocation);

        if (!control) {
            return '';
        }

        const field = this._fields.find(_field => _field.control === control.id);

        if (!field) {
            return '';
        }

        return field.value;

    }

    get isChangePending(): boolean {
        return this._changesPending;
    }

    get jobReference(): string {
        return this.job ? this.job.reference : '';
    }

    get jobModified(): string {
        return this.job ? this.job.modified : '';
    }

    get jobModifiedBy(): string {
        return this._modifiedByUser ? this._modifiedByUser.name : '';
    }

    get jobCreated(): string {
        return this.job ? this.job.created : '';
    }

    get jobCreatedBy(): string {
        return this._createdByUser ? this._createdByUser.name : '';
    }

    get jobStatusId(): string {
        return this._jobStatus.id;
    }

    get jobStatusTitle(): string {
        return this._jobStatus.title;
    }

    get jobTypeTitle(): string {
        return this._jobType ? this._jobType.title : '';
    }

    get jobPrevious(): IJobPointer {
        return this.job ? this.job.previous : {} as any;
    }

    get jobNext(): Array<IJobPointer> {
        return this.job ? this.job.next : [];
    }

    get jobAssigneeId(): string {
        return this.job.assigneeId;
    }

    get navigateUrl(): string {
        const location = this.jobLocation;

        if (!location) {
            return '';
        }

        return `https://www.google.com/maps/search/?api=1&query=${encodeURI(location)}`;
    }

    get taskCount(): number {
        return this._tasks ? this._tasks.length : 0;
    }

    get userIsAdmin(): boolean {
        return this._activeMembership ? this._activeMembership.admin : false;
    }

    get canSeeAuditLogs(): boolean {
        return this._activeMembership.admin;
    }

    get pointers(): Array<JobPointerModel> {
        return [...this.fieldPointers, this.previousPointer, ...this.nextPointers].filter(p => !!p);
    }

    get fieldPointers(): Array<JobPointerModel> {

        return this.fields
            .filter(field => field.fieldJobPointers.length > 0)
            .map(field => field.fieldJobPointers.map(pointer => new JobPointerModel({
                type: JobPointerEnum.FIELD,
                pointer,
                control: this.getControlById(field.control as string),
                sourceJob: this.job
            })))
            .reduce((c, p) => {
                return p.concat(c);
            }, []);
    }

    get nextPointers(): Array<JobPointerModel> {
        return this.job.next
            .map(pointer => new JobPointerModel({
                type: JobPointerEnum.NEXT,
                pointer,
                sourceJob: this.job
            }));
    }

    get previousPointer(): JobPointerModel | undefined {

        if (!this.job.previous) {
            return undefined;
        }

        return new JobPointerModel({
            type: JobPointerEnum.PREVIOUS,
            pointer: this.job.previous,
            sourceJob: this.job
        });
    }

    get reportCount(): number {
        return this.job.reports.length;
    }

    get subheaders(): Array<{label: string, control: Control, value: string}> {

        return this._controls
            .filter(control => control.config.includeInJobCard)
            .map(control => {

                const field = this._fields.find(f => f.control === control.id);

                return {
                    label: control.title,
                    control,
                    value: field ? field.value : ''
                };
            });

    }

    getControlById(id: string): Control {
        return this._controls.find(control => control.id === id);
    }

    getFieldByControlName(name: string): Field {
        return this._fields
            .find(field => {
                const control = this.getControlById(field.control as string);
                if (!control) {
                    return;
                }
                return control.name === name;
            });
    }

    getValueByControlName(name: string): string {

        const field = this.getFieldByControlName(name);

        if (!field) {
            return '';
        }

        return field.value;
    }
}



// WEBPACK FOOTER //
// ./src/app/molecules/job-card/job-card.model.ts